import { Collapse, Typography } from '@material-ui/core';
import { FormikTextField } from '@superdispatch/forms';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { joinStrings } from 'shared/utils/StringUtils';
import { loadVenueSchema } from '../data/LoadDTO';
import { FormikVenueAddressAutofill } from './FormikVenueAddressAutofill';
import {
  FormikVenueAddressSuggestion,
  useVenueAddressState,
} from './FormikVenueAddressSuggestion';
import { VenueAddressVerificationProvider } from './VenueAddressVerificationContext';
import { FormikVenueAutofill } from './FormikVenueAutofill';
import { Box } from '@superdispatch/ui-lab';
import { Info } from '@material-ui/icons';

interface LoadCustomerFormProps {
  name?: string;
  disabled?: boolean;
  canEditAddress?: boolean | null;
  isSubhauled?: boolean | null;
  isPrimeCarrierSubhaulLoadEditPage?: boolean;
}

export function LoadCustomerForm({
  name,
  disabled,
  canEditAddress = true,
  isSubhauled = false,
  isPrimeCarrierSubhaulLoadEditPage,
}: LoadCustomerFormProps) {
  const prefix = !name ? '' : `${name}.`;
  const { verifiedAddress, setVerifiedAddress } = useVenueAddressState(
    `${prefix}venue`,
  );

  return (
    <Stack space="large">
      {canEditAddress ? (
        <FormikVenueAutofill
          fullWidth={true}
          label="Shipper Name"
          name={`${prefix}venue`}
          schema={loadVenueSchema}
          inputProps={{ maxLength: 100 }}
          disabled={
            disabled || !!isSubhauled || isPrimeCarrierSubhaulLoadEditPage
          }
        />
      ) : (
        <FormikTextField
          fullWidth={true}
          label="Shipper Name"
          name={`${prefix}venue.name`}
          disabled={
            disabled || !!isSubhauled || isPrimeCarrierSubhaulLoadEditPage
          }
        />
      )}

      <VenueAddressVerificationProvider
        name={`${prefix}venue`}
        verifiedAddress={verifiedAddress}
        onVerify={setVerifiedAddress}
      >
        <Stack space="small">
          <FormikVenueAddressAutofill
            fullWidth={true}
            label="Street Address"
            name={`${prefix}venue`}
            disabled={
              disabled ||
              !canEditAddress ||
              !!isSubhauled ||
              isPrimeCarrierSubhaulLoadEditPage
            }
            onGeocode={(geocoding) => {
              setVerifiedAddress(
                joinStrings(' ', geocoding.house_number, geocoding.address),
              );
            }}
          />

          <Columns space="small" collapseBelow="desktop">
            <Column width="1/2">
              <FormikTextField
                fullWidth={true}
                label="City"
                name={`${prefix}venue.city`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  !!isSubhauled ||
                  isPrimeCarrierSubhaulLoadEditPage
                }
              />
            </Column>

            <Column width="1/4">
              <FormikTextField
                fullWidth={true}
                label="State"
                name={`${prefix}venue.state`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  !!isSubhauled ||
                  isPrimeCarrierSubhaulLoadEditPage
                }
              />
            </Column>

            <Column width="1/4">
              <FormikTextField
                fullWidth={true}
                label="ZIP"
                name={`${prefix}venue.zip`}
                disabled={
                  disabled ||
                  !canEditAddress ||
                  !!isSubhauled ||
                  isPrimeCarrierSubhaulLoadEditPage
                }
              />
            </Column>
          </Columns>

          <Collapse in={!canEditAddress && !isSubhauled} unmountOnExit={true}>
            <Box padding="xsmall" borderRadius="small" backgroundColor="Blue50">
              <Columns space="xsmall" align="center">
                <Column width="content">
                  <Info fontSize="small" color="primary" />
                </Column>
                <Column width="fluid">
                  <Typography>
                    Location set by the shipper cannot be changed. Contact
                    shipper to update.
                  </Typography>
                </Column>
              </Columns>
            </Box>
          </Collapse>
          <FormikVenueAddressSuggestion />
        </Stack>
      </VenueAddressVerificationProvider>

      <Stack space="small">
        <Typography variant="h4">Contact Details</Typography>

        <FormikTextField
          fullWidth={true}
          label="Full Name"
          name={`${prefix}venue.contacts[0].name`}
          disabled={
            disabled || !!isSubhauled || isPrimeCarrierSubhaulLoadEditPage
          }
        />

        {!isPrimeCarrierSubhaulLoadEditPage && (
          <FormikTextField
            fullWidth={true}
            label="Billing Email"
            name={`${prefix}venue.billing_email`}
            disabled={!!isSubhauled}
          />
        )}

        <Columns space="small" collapseBelow="desktop">
          <Column width="1/2">
            <FormikTextField
              fullWidth={true}
              label="Phone"
              name={`${prefix}venue.contacts[0].phone`}
              disabled={
                disabled || !!isSubhauled || isPrimeCarrierSubhaulLoadEditPage
              }
            />
          </Column>
          <Column width="1/2">
            <FormikTextField
              fullWidth={true}
              label="Email"
              name={`${prefix}venue.contacts[0].email`}
              disabled={
                disabled || !!isSubhauled || isPrimeCarrierSubhaulLoadEditPage
              }
            />
          </Column>
        </Columns>
      </Stack>
    </Stack>
  );
}
